import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePrevious } from "../../../../global_functions/hooks";

export const isLeftNavCollapsedAtom = atom(false);
export const useIsLeftNavCollapsed = () => useAtomValue(isLeftNavCollapsedAtom);

// Set of paths that are expanded
const expandedPathsAtom = atom(new Set<string>());
const addExpandedPathsAtom = atom(
  (get) => get(expandedPathsAtom),
  (get, set, newPaths: Set<string>) => {
    set(expandedPathsAtom, new Set([...get(expandedPathsAtom), ...newPaths]));
  }
);
export const useIsPathExpanded = (path: string) =>
  useAtomValue(expandedPathsAtom).has(path.replace(/\/$/, ""));

export const useSetPathExpanded = (
  path: string,
  opts: { collapseSiblingsOnExpand?: boolean } = {}
) => {
  const [state, setState] = useAtom(expandedPathsAtom);
  const trimmedPath = path.replace(/\/$/, "");

  return useCallback(
    (expanded: boolean) => {
      const prevExpanded = state.has(trimmedPath);
      if (prevExpanded === expanded) {
        return;
      }

      const newState = new Set(state);
      if (expanded) {
        newState.add(trimmedPath);
        if (opts.collapseSiblingsOnExpand) {
          collapseSiblings(newState, trimmedPath);
        }
      } else {
        newState.delete(trimmedPath);
      }

      setState(newState);
    },
    [state, setState, trimmedPath, opts.collapseSiblingsOnExpand]
  );
};

// Modifies state in place, deleting siblings for given path
const collapseSiblings = (state: Set<string>, path: string) => {
  const trimmedPath = path.replace(/\/$/, "");
  // This may need to be improved if siblings stop matching this heuristic
  const root = trimmedPath.split("/").slice(0, -1).join("/");
  state.forEach((expandedPath) => {
    if (
      // Keep the path and any of its children open
      !expandedPath.includes(trimmedPath) &&
      // Keep the root path expanded (parent)
      expandedPath !== root &&
      // Close paths that have the same root (siblings)
      expandedPath.includes(root)
    ) {
      state.delete(expandedPath);
    }
  });
  return state;
};

export const useCollapseSiblingsOnPathChange = (
  path: string,
  enabled: boolean
) => {
  const { pathname } = useLocation();
  const [state, setState] = useAtom(expandedPathsAtom);
  const isExpanded = state.has(path);
  const prevExpanded = usePrevious(isExpanded);
  const expandedByPathChange = pathname.includes(path);

  useEffect(() => {
    // Collapse siblings if path change caused expand
    if (enabled && isExpanded && !prevExpanded && expandedByPathChange) {
      const newState = new Set(state);
      collapseSiblings(newState, path);
      setState(newState);
    }
  }, [
    enabled,
    isExpanded,
    prevExpanded,
    expandedByPathChange,
    path,
    state,
    setState,
  ]);
};

export const useAutoExpandOnPathChange = () => {
  const { pathname } = useLocation();
  const addExpandedPaths = useSetAtom(addExpandedPathsAtom);
  const lastPathname = useRef<string>();

  useEffect(() => {
    if (lastPathname.current !== pathname) {
      const newPaths = new Set<string>();
      const parts = pathname.split("/");
      for (let i = 0; i < parts.length; i++) {
        const subpath = parts.slice(0, i + 1).join("/");
        if (subpath && subpath !== "/") {
          newPaths.add(subpath);
        }
      }
      addExpandedPaths(newPaths);
      lastPathname.current = pathname;
    }
  }, [pathname, addExpandedPaths]);
};
